import React from 'react';
import { HiOutlineDownload, HiOutlinePaperClip, HiOutlinePlus, HiOutlineTrash } from 'react-icons/hi';
import { AttachmentDelivery, EmailAttachment, TemplateVariable } from '../../../../../../types/communication';
import { DEFAULT_LINK_EXPIRATION_MINUTES } from '../../../../../../utils/emailEnvelope';
import SelectTextField from '../../../../../molecules/SelectTextField';
import TokenListField from '../../shared/TokenListField';
import VariableTextField from '../../shared/VariableTextField';

interface EmailAttachmentsEditorProps {
    attachments: EmailAttachment[];
    variables: TemplateVariable[];
    disabled: boolean;
    onChange: (attachments: EmailAttachment[]) => void;
}

const EXPIRATIONS = [
    { minutes: 60, label: '1 hora' },
    { minutes: 24 * 60, label: '1 dia' },
    { minutes: 7 * 24 * 60, label: '7 dias' },
    { minutes: 30 * 24 * 60, label: '30 dias' },
];

const DELIVERY_OPTIONS: { key: AttachmentDelivery; label: string; hint: string; icon: React.ReactNode }[] = [
    { key: 'ATTACHMENT', label: 'Anexo', hint: 'O arquivo vai junto no e-mail', icon: <HiOutlinePaperClip size={14} /> },
    { key: 'LINK', label: 'Link para download', hint: 'Um link que expira, bom para arquivos grandes', icon: <HiOutlineDownload size={14} /> },
];

const EmailAttachmentsEditor: React.FC<EmailAttachmentsEditorProps> = ({ attachments, variables, disabled, onChange }) => {
    const fileVariables = variables.filter((v) => v.format === 'FILE_URL');
    const update = (index: number, patch: Partial<EmailAttachment>) =>
        onChange(attachments.map((a, i) => (i === index ? { ...a, ...patch } : a)));

    return (
        <div className="flex flex-col gap-3">
            {attachments.map((attachment, index) => (
                <div key={index} className="rounded-xl border border-gray-200 dark:border-slate-700 p-3">
                    <div className="grid grid-cols-1 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)_auto] gap-3 items-end">
                        <TokenListField
                            id={`attachment-source-${index}`}
                            label="Arquivo (link ou variável)"
                            values={attachment.source ? [attachment.source] : []}
                            variables={fileVariables.length ? fileVariables : variables}
                            disabled={disabled}
                            max={1}
                            placeholder="Cole um link ou insira uma variável"
                            error={!disabled && !attachment.source.trim() ? 'Obrigatório' : undefined}
                            onChange={(values) => update(index, { source: values[0] ?? '' })}
                        />
                        <VariableTextField
                            id={`attachment-name-${index}`}
                            label="Nome do arquivo (opcional)"
                            value={attachment.fileName}
                            variables={variables}
                            disabled={disabled}
                            onChange={(fileName) => update(index, { fileName })}
                        />
                        {!disabled && (
                            <button
                                type="button"
                                onClick={() => onChange(attachments.filter((_, i) => i !== index))}
                                className="mb-2 text-gray-400 hover:text-red-600"
                                aria-label="Remover arquivo"
                            >
                                <HiOutlineTrash size={16} />
                            </button>
                        )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                        {DELIVERY_OPTIONS.map((option) => {
                            const active = attachment.delivery === option.key;
                            return (
                                <button
                                    key={option.key}
                                    type="button"
                                    disabled={disabled}
                                    title={option.hint}
                                    onClick={() =>
                                        update(index, {
                                            delivery: option.key,
                                            expirationMinutes:
                                                option.key === 'LINK' ? attachment.expirationMinutes ?? DEFAULT_LINK_EXPIRATION_MINUTES : undefined,
                                        })
                                    }
                                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs border ${
                                        active
                                            ? 'bg-slate-700 border-slate-700 text-white'
                                            : 'border-gray-300 dark:border-slate-600 text-gray-600 dark:text-gray-300 hover:border-gray-400'
                                    }`}
                                >
                                    {option.icon}
                                    {option.label}
                                </button>
                            );
                        })}
                        {attachment.delivery === 'LINK' && (
                            <SelectTextField
                                id={`attachment-expiration-${index}`}
                                label="Link expira em"
                                value={String(attachment.expirationMinutes ?? DEFAULT_LINK_EXPIRATION_MINUTES)}
                                disabled={disabled}
                                hideEmptyOption
                                className="w-40 ml-2"
                                items={EXPIRATIONS.map((option) => ({ value: String(option.minutes), label: option.label }))}
                                onChange={(e) => update(index, { expirationMinutes: Number(e.target.value) })}
                            />
                        )}
                    </div>
                </div>
            ))}
            {!attachments.length && <p className="text-sm text-gray-400">Nenhum arquivo.</p>}
            {!disabled && (
                <button
                    type="button"
                    onClick={() => onChange([...attachments, { source: '', fileName: '', delivery: 'ATTACHMENT' }])}
                    className="self-start inline-flex items-center gap-1 text-xs text-teal-700 dark:text-teal-300 hover:underline"
                >
                    <HiOutlinePlus size={14} /> Adicionar arquivo
                </button>
            )}
        </div>
    );
};

export default EmailAttachmentsEditor;
